/**
 * VariantQtyTable — operator allocates a fixed total quantity across
 * the variants of a product. Each row shows the variant axes label +
 * current on-hand stock + a numeric input for the qty being added. A
 * live "Remaining" indicator decrements as inputs are typed.
 *
 * Lifted from the (now-purged) Stock Entry page on 2026-06-03 and
 * generalised: the Receive modal on Warehouse > Product Purchases
 * uses the same UI, with an externally-supplied capacity to cap.
 *
 * Pure presentational — owns no state, no fetch. The parent owns the
 * per-variant qty map and the validation / submit flow.
 */

import { Badge } from '../atoms/Badge'
import { Input } from '../atoms/Input'
import { Th, Td } from './Table'
import { TableShell } from './TableShell'

export interface VariantQtyRow {
  readonly variant_id: string
  readonly axes_label: string | null
  readonly on_hand: number
  readonly is_low_stock?: boolean
}

interface Props {
  readonly variants: ReadonlyArray<VariantQtyRow>
  /** Per-variant qty entered so far (whole units, integer ≥ 0). */
  readonly qtyByVariant: Readonly<Record<string, number>>
  readonly onQtyChange: (variantId: string, qty: number) => void
  /**
   * Whole-units capacity the operator is allocating. When provided, a
   * "Remaining: N" cell shows on each row (the same value, since the
   * total is invoice-wide, not per-row). Required for the Receive
   * flow; omit if the parent doesn't want the per-row counter.
   */
  readonly remaining?: number
  readonly disabled?: boolean
}

function parseQtyInput(raw: string): number {
  const t = raw.trim()
  if (t === '') return 0
  const n = Number(t)
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return NaN
  return n
}

export function VariantQtyTable({
  variants,
  qtyByVariant,
  onQtyChange,
  remaining,
  disabled = false
}: Props): JSX.Element {
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Variant</Th>
            <Th className="text-right">On hand</Th>
            <Th className="text-right">Receive qty</Th>
            {remaining !== undefined ? <Th className="text-right">Remaining</Th> : null}
          </tr>
        </thead>
        <tbody>
          {variants.map((v) => {
            const current = qtyByVariant[v.variant_id] ?? 0
            return (
              <tr key={v.variant_id} className="border-t border-border">
                <Td>
                  {v.axes_label === null || v.axes_label === '' ? (
                    <span className="text-text-secondary">default</span>
                  ) : (
                    v.axes_label
                  )}
                </Td>
                <Td className="text-right whitespace-nowrap font-mono">
                  {v.on_hand}
                  {v.is_low_stock === true ? <span className="ml-2"><Badge tone="error">Low stock</Badge></span> : null}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  <Input
                    value={current === 0 ? '' : String(current)}
                    onChange={(e) => {
                      const parsed = parseQtyInput(e.target.value)
                      onQtyChange(v.variant_id, Number.isNaN(parsed) ? 0 : parsed)
                    }}
                    disabled={disabled}
                    inputMode="numeric"
                    placeholder="0"
                    aria-label={`Receive qty for ${v.axes_label ?? 'default'}`}
                    className="w-24 text-right"
                  />
                </Td>
                {remaining !== undefined ? (
                  <Td className="text-right whitespace-nowrap font-mono">{remaining}</Td>
                ) : null}
              </tr>
            )
          })}
        </tbody>
      </table>
    </TableShell>
  )
}
